// ── Stone image helpers (shared by chat cards and the Search Results page) ────
//
// Some data reuses image links across stones (e.g. a white emerald's photo on
// a yellow emerald). Photo links carry their own stock ID (as the file name or
// a folder), so a link is only trusted when it names this stone. Everything
// else gets a sample picture, labelled as such in the UI.

const FALLBACK_SHAPES = new Set([
  "round", "oval", "pear", "cushion", "emerald", "princess",
  "radiant", "marquise", "heart",
]);
const FALLBACK_COLORS = new Set([
  "brown", "pink", "blue", "yellow", "green", "orange", "gray", "purple",
]);

// Sample pictures that actually exist in frontend/public/diamonds/
const FALLBACK_FILES = new Set([
  "cushion_green", "cushion_orange", "cushion_pink", "cushion_purple",
  "emerald_yellow",
  "heart_orange", "heart_pink", "heart_purple",
  "marquise_brown", "marquise_green", "marquise_orange", "marquise_pink", "marquise_yellow",
  "oval_green", "oval_orange", "oval_pink", "oval_purple",
  "pear_blue", "pear_gray", "pear_green", "pear_orange", "pear_pink", "pear_purple", "pear_yellow",
  "princess_orange", "princess_yellow",
  "radiant_orange", "radiant_pink", "radiant_purple", "radiant_yellow",
]);

// Tint for the drawn placeholder when no sample picture exists
const PLACEHOLDER_TINTS = {
  normal: "#d1d5db", yellow: "#facc15", pink: "#f472b6", blue: "#60a5fa",
  green: "#4ade80", orange: "#fb923c", brown: "#a16207", gray: "#9ca3af",
  purple: "#a78bfa",
};

function colorKeyOf(isFancy, fancyColor) {
  const fc = (fancyColor || "").toLowerCase().trim();
  return isFancy && FALLBACK_COLORS.has(fc) ? fc : "normal";
}

export function placeholderSvg(isFancy, fancyColor) {
  const tint = PLACEHOLDER_TINTS[colorKeyOf(isFancy, fancyColor)];
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
    `<path d="M16 10h32l12 14-28 32L4 24z" fill="${tint}" fill-opacity="0.35" stroke="${tint}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="M4 24h56M16 10l8 14 8-14 8 14 8-14M24 24l8 32 8-32" fill="none" stroke="${tint}" stroke-width="1.5" stroke-linejoin="round"/>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function sampleImage(shape, isFancy, fancyColor) {
  const shapeKey = (shape || "").toLowerCase().trim();
  const safeShape = FALLBACK_SHAPES.has(shapeKey) ? shapeKey : "round";
  const file = `${safeShape}_${colorKeyOf(isFancy, fancyColor)}`;
  return FALLBACK_FILES.has(file) ? `/diamonds/${file}.png` : placeholderSvg(isFancy, fancyColor);
}

// True when the image link is this stone's own photo: the stock ID is the file
// name (".../5104703.jpg") or a folder in the path (".../imaged/L241-307A/still.jpg")
export function isOwnPhoto(url, stockId) {
  if (!url || !stockId) return false;
  const id = String(stockId).toUpperCase();
  const parts = url.split("?")[0].split("/").map((p) => p.toUpperCase());
  const name = parts[parts.length - 1].replace(/\.[A-Z0-9]+$/, "");
  return name === id || parts.includes(id);
}

// { src, isReal } — the stone's own photo, or a sample picture
export function stoneImage(stone) {
  if (isOwnPhoto(stone.img_url, stone.stock_id)) {
    return { src: stone.img_url, isReal: true };
  }
  return { src: sampleImage(stone.shape, stone.is_fancy_color, stone.fancy_color), isReal: false };
}
